import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  CalendarCheck,
  CalendarClock,
  CircleArrowLeft,
  LayoutDashboard,
  Lock,
  PanelLeft,
  PanelLeftClose,
  Plus,
  Save,
  Settings,
  Unlock,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useStudioStore } from '~entities/studios/model/studio-store';

import {
  confirmScheduleAPI,
  createCampaignAPI,
  createCampaignTemplateAPI,
  // createTemplateFromUnlayerAPI,
  getCampaignAPI,
  getCampaignTemplateAPI,
  listCampaignsAPI,
  previewAudienceAPI,
  unlockScheduleAPI,
  updateCampaignAPI,
  updateCampaignTemplateAPI,
  updateComposerDefaultsAPI,
} from '~features/email-builder/api/email-builder-api';
import {
  useCampaign,
  useCampaignTemplates,
  useComposerDefaults,
  useUnlayerIdentity,
  // useUnlayerTemplates,
} from '~features/email-builder/api/hooks';
import { useEmailDraftStore } from '~features/email-builder/model/draft-store';
import type {
  AudiencePreview,
  EmailCampaign,
  EmailCampaignStatus,
  EmailCampaignTemplate,
  EmailCampaignTemplateCategory,
} from '~features/email-builder/model/types';
import { MultiClassAudienceSelector } from '~features/email-builder/ui/multi-class-audience-selector';
import { MultiEventAudienceSelector } from '~features/email-builder/ui/multi-event-audience-selector';
import { AudiencePreviewTable } from '~features/email-builder/ui/audience-preview-table';
import { StudioTemplatesPanel } from '~features/email-builder/ui/studio-templates-panel';
import {
  UnlayerEditor,
  type UnlayerEditorHandle,
} from '~features/email-builder/ui/unlayer-editor';
// import { UnlayerStartersPanel } from '~features/email-builder/ui/unlayer-starters-panel';
import { TagsSelector } from '~features/shared/tags-selector';

import { QUERY_KEYS } from '~shared/api/query-keys';
import { DateTimePicker } from '~shared/components/date-time-picker';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

const CATEGORIES: Array<EmailCampaignTemplateCategory> = [
  'recital',
  'camp',
  'weather',
  'general',
  'custom',
];

const CAMPAIGN_PANEL_STORAGE_KEY = 'enrollio-email-builder-campaign-panel';

/** Must match BE `EMAIL_CAMPAIGN_SCHEDULE_MIN_LEAD_MS`. */
const SCHEDULE_MIN_LEAD_MS = 20 * 60 * 1000;

/** Maps draft schedule to PATCH/POST body (ISO instant). Does not lock or enqueue. */
const scheduleAtForApi = (
  scheduleAt: string | null,
): { scheduleAt: string | null } | Record<string, never> => {
  if (scheduleAt === null) {
    return { scheduleAt: null };
  }
  if (!scheduleAt.trim()) {
    return {};
  }
  const ms = new Date(scheduleAt).getTime();
  if (!Number.isFinite(ms)) {
    return {};
  }
  return { scheduleAt: new Date(scheduleAt).toISOString() };
};

const campaignScheduleToIso = (
  scheduleAt: string | undefined,
): string | null => {
  if (!scheduleAt) {
    return null;
  }
  const ms = new Date(scheduleAt).getTime();
  if (!Number.isFinite(ms)) {
    return null;
  }
  return new Date(scheduleAt).toISOString();
};

const readCampaignPanelOpen = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }
  return localStorage.getItem(CAMPAIGN_PANEL_STORAGE_KEY) !== '0';
};

const hasAudienceSelection = (draft: {
  tagOptions: Array<{ value: string }>;
  classOptions: Array<{ value: string }>;
  eventOptions: Array<{ value: string }>;
}) =>
  draft.tagOptions.length > 0 ||
  draft.classOptions.length > 0 ||
  draft.eventOptions.length > 0;

const snapshotTemplateAudience = (draft: {
  tagOptions: Array<{ label: string; value: string }>;
  classOptions: Array<{ label: string; value: string }>;
  eventOptions: Array<{ label: string; value: string }>;
}) => ({
  tags: draft.tagOptions.map((t) => ({
    id: String(t.value),
    label: t.label,
  })),
  classes: draft.classOptions.map((c) => ({
    id: String(c.value),
    label: c.label,
  })),
  events: draft.eventOptions.map((e) => ({
    id: String(e.value),
    label: e.label,
  })),
});

const applyTemplateAudience = (
  draft: {
    setTagOptions: (tags: Array<{ label: string; value: string }>) => void;
    setClassOptions: (
      classes: Array<{ label: string; value: string }>,
    ) => void;
    setEventOptions: (
      events: Array<{ label: string; value: string }>,
    ) => void;
  },
  audience: EmailCampaignTemplate['audience'] | undefined,
) => {
  draft.setTagOptions(
    (audience?.tags ?? []).map((t) => ({
      label: t.label,
      value: String(t.id),
    })),
  );
  draft.setClassOptions(
    (audience?.classes ?? []).map((c) => ({
      label: c.label,
      value: String(c.id),
    })),
  );
  draft.setEventOptions(
    (audience?.events ?? []).map((e) => ({
      label: e.label,
      value: String(e.id),
    })),
  );
};

export const EmailBuilderPage = () => {
  const studioId = useStudioStore((s) => s.currentStudioId);
  const queryClient = useQueryClient();
  const editorRef = useRef<UnlayerEditorHandle | null>(null);
  const schedulePersistGenerationRef = useRef(0);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<AudiencePreview | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] =
    useState<EmailCampaignTemplateCategory>('general');
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [campaignPanelOpen, setCampaignPanelOpen] = useState(
    readCampaignPanelOpen,
  );

  const draft = useEmailDraftStore();

  useEffect(() => {
    localStorage.setItem(
      CAMPAIGN_PANEL_STORAGE_KEY,
      campaignPanelOpen ? '1' : '0',
    );
    const resizeTimer = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);
    return () => {
      window.clearTimeout(resizeTimer);
    };
  }, [campaignPanelOpen]);
  const defaultsQuery = useComposerDefaults();
  const templatesQuery = useCampaignTemplates();
  const unlayerIdentityQuery = useUnlayerIdentity();
  const unlayerIdentityErrorMessage = (() => {
    if (!unlayerIdentityQuery.isError) {
      return null;
    }
    const error = unlayerIdentityQuery.error;
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    return 'We could not open the email editor. Please refresh and try again.';
  })();
  // const unlayerTemplatesQuery = useUnlayerTemplates();
  const campaignQuery = useCampaign(draft.campaignId ?? undefined);

  const isCampaignEditable = (status: EmailCampaignStatus | null) => {
    if (!status) {
      return true;
    }
    return !['sent', 'sending', 'cancelled', 'failed'].includes(status);
  };

  const applyCampaignScheduleToDraft = (campaign: EmailCampaign) => {
    const iso = campaignScheduleToIso(campaign.scheduleAt);
    if (iso) {
      draft.setScheduleAt(iso);
    }
  };

  const scheduleContextMatches = (expected: {
    activeTemplateId: string | null;
    campaignId: string | null;
  }) => {
    const state = useEmailDraftStore.getState();
    if (state.activeTemplateId !== expected.activeTemplateId) {
      return false;
    }
    if (expected.campaignId === null) {
      return true;
    }
    return state.campaignId === expected.campaignId;
  };

  const persistTemplateScheduleAt = async (scheduleAt: string | null) => {
    const state = useEmailDraftStore.getState();
    if (!state.activeTemplateId || state.scheduleLocked) {
      return;
    }
    try {
      const updated = await updateCampaignTemplateAPI(state.activeTemplateId, {
        ...scheduleAtForApi(scheduleAt),
      });
      const iso = campaignScheduleToIso(updated.scheduleAt);
      if (iso) {
        draft.setScheduleAt(iso);
      } else if (scheduleAt === null) {
        draft.setScheduleAt(null);
      }
    } catch {
      // Update Campaign will retry.
    }
  };

  const persistDraftScheduleAt = async (scheduleAt: string | null) => {
    const state = useEmailDraftStore.getState();
    if (
      !state.campaignId ||
      state.scheduleLocked ||
      !isCampaignEditable(state.campaignStatus)
    ) {
      return;
    }
    try {
      const updated = await updateCampaignAPI(
        state.campaignId,
        scheduleAtForApi(scheduleAt),
      );
      if (scheduleAt === null) {
        draft.setScheduleAt(null);
      } else {
        applyCampaignScheduleToDraft(updated);
      }
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMAIL_CAMPAIGN(studioId, state.campaignId),
      });
    } catch {
      // Confirm schedule / ensureCampaign will retry.
    }
  };

  /** Creates a server send draft so schedule time can persist before Audience export. */
  const ensureSendDraftForSchedule = async (scheduleAt: string | null) => {
    if (!scheduleAt) {
      return;
    }
    const state = useEmailDraftStore.getState();
    if (
      state.campaignId ||
      state.scheduleLocked ||
      !isCampaignEditable(state.campaignStatus)
    ) {
      return;
    }
    if (!state.subject.trim() || !hasAudienceSelection(state)) {
      return;
    }
    try {
      const created = await createCampaignAPI({
        subject: state.subject.trim(),
        templateId: state.activeTemplateId ?? undefined,
        audience: {
          tagIds: state.tagOptions.map((t) => String(t.value)),
          classIds: state.classOptions.map((c) => String(c.value)),
          eventIds: state.eventOptions.map((e) => String(e.value)),
          excludeParentIds: [],
        },
        ...scheduleAtForApi(scheduleAt),
      });
      draft.setCampaignId(created._id);
      draft.setCampaignStatus(created.status);
      applyCampaignScheduleToDraft(created);
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMAIL_CAMPAIGN(studioId, created._id),
      });
    } catch {
      // ensureCampaign on Audience will create the draft later.
    }
  };

  const campaignStatus = (draft.campaignStatus ??
    campaignQuery.data?.status ??
    null) as EmailCampaignStatus | null;
  const isCampaignComplete = !isCampaignEditable(campaignStatus);

  useEffect(() => {
    if (defaultsQuery.data) {
      draft.hydrateFromDefaults({
        fromName: defaultsQuery.data.fromName || '',
        replyToEmail: defaultsQuery.data.replyToEmail || '',
        batchSize: defaultsQuery.data.batchSize || 50,
        openTracking: defaultsQuery.data.openTracking,
        clickTracking: defaultsQuery.data.clickTracking,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once when defaults load
  }, [defaultsQuery.data]);

  useEffect(() => {
    const campaign = campaignQuery.data;
    const campaignId = useEmailDraftStore.getState().campaignId;
    if (!campaign || campaign._id !== campaignId) {
      return;
    }
    draft.setCampaignStatus(campaign.status);
    draft.setScheduleLocked(Boolean(campaign.scheduleLockedAt));
    applyCampaignScheduleToDraft(campaign);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from server campaign
  }, [campaignQuery.data, draft.campaignId]);

  const invalidateCampaigns = () => {
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.EMAIL_CAMPAIGNS(studioId),
    });
    if (draft.campaignId) {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMAIL_CAMPAIGN(studioId, draft.campaignId),
      });
    }
  };

  // Never fall back to the stored draft: a missing editor means we would
  // persist content that no longer matches what the studio sees on screen.
  const exportEditorContent = async () => {
    const editor = editorRef.current;
    if (!editor) {
      throw new Error('Email editor is still loading. Try again in a moment.');
    }
    return editor.exportHtml();
  };

  const ensureCampaign = async (_options?: { previewOnly?: boolean }) => {
    // Always read latest draft fields (avoids stale closures after awaits).
    // exportHtml already returns design JSON — no need for a separate saveDesign.
    const beforeExport = useEmailDraftStore.getState();
    const statusBefore =
      beforeExport.campaignStatus ?? campaignQuery.data?.status ?? null;
    const lockedBefore =
      beforeExport.scheduleLocked &&
      statusBefore === 'scheduled' &&
      Boolean(beforeExport.campaignId);

    // Locked scheduled campaigns freeze subject/design/audience until unlock.
    // Skip editor export + PATCH so we do not show local edits that BE rejects.
    if (lockedBefore && beforeExport.campaignId) {
      return getCampaignAPI(beforeExport.campaignId);
    }

    const { design: designJson, html } = await exportEditorContent();
    const afterExport = useEmailDraftStore.getState();
    const subject = afterExport.subject;

    draft.setDesignJson(designJson);
    if (html) {
      draft.setHtml(html);
    }

    const audience = {
      tagIds: afterExport.tagOptions.map((t) => String(t.value)),
      classIds: afterExport.classOptions.map((c) => String(c.value)),
      eventIds: afterExport.eventOptions.map((e) => String(e.value)),
      excludeParentIds: [] as Array<string>,
    };

    // Always use persistent studio defaults at send time (no per-campaign override).
    const settingsOverride = {};

    const status =
      afterExport.campaignStatus ?? campaignQuery.data?.status ?? null;
    let campaignId = afterExport.campaignId;

    // Terminal / in-flight campaigns can't be patched — start a fresh draft.
    if (campaignId && !isCampaignEditable(status)) {
      draft.setCampaignId(null);
      draft.setCampaignStatus(null);
      draft.setScheduleLocked(false);
      campaignId = null;
    }

    if (campaignId) {
      const updated = await updateCampaignAPI(campaignId, {
        subject,
        designJson,
        html,
        audience,
        settingsOverride,
        ...scheduleAtForApi(afterExport.scheduleAt),
        // Omit null so PATCH does not clear a server-side templateId.
        ...(afterExport.activeTemplateId
          ? { templateId: afterExport.activeTemplateId }
          : {}),
      });
      draft.setCampaignStatus(updated.status);
      applyCampaignScheduleToDraft(updated);
      return updated;
    }

    const created = await createCampaignAPI({
      subject,
      designJson,
      html,
      audience,
      settingsOverride,
      templateId: afterExport.activeTemplateId ?? undefined,
      ...scheduleAtForApi(afterExport.scheduleAt),
    });
    draft.setCampaignId(created._id);
    draft.setCampaignStatus(created.status);
    applyCampaignScheduleToDraft(created);
    return created;
  };

  const previewMutation = useMutation({
    mutationFn: async () => {
      const before = useEmailDraftStore.getState();
      if (!hasAudienceSelection(before)) {
        throw new Error(
          'Select at least one tag, class, or event for the audience',
        );
      }
      const campaign = await ensureCampaign({ previewOnly: true });
      const after = useEmailDraftStore.getState();
      if (!hasAudienceSelection(after)) {
        throw new Error(
          'Select at least one tag, class, or event for the audience',
        );
      }
      return previewAudienceAPI(campaign._id);
    },
    onSuccess: (data) => {
      setAudienceCount(data.count);
      setPreviewData(data);
      setPreviewOpen(true);
    },
    onError: (err: Error) => toast.error(err.message || 'Preview failed'),
  });

  const confirmScheduleMutation = useMutation({
    mutationFn: async () => {
      const before = useEmailDraftStore.getState();
      if (!hasAudienceSelection(before)) {
        throw new Error(
          'Select at least one tag, class, or event for the audience',
        );
      }
      if (!before.subject.trim()) {
        throw new Error('Enter an email subject before scheduling');
      }
      if (!before.scheduleAt) {
        throw new Error('Pick a schedule date/time first');
      }
      const campaign = await ensureCampaign();
      // Re-read after awaits — scheduleAt/subject may have changed mid-flight.
      const after = useEmailDraftStore.getState();
      if (!hasAudienceSelection(after)) {
        throw new Error(
          'Select at least one tag, class, or event for the audience',
        );
      }
      if (!after.subject.trim()) {
        throw new Error('Enter an email subject before scheduling');
      }
      if (!after.scheduleAt) {
        throw new Error('Pick a schedule date/time first');
      }
      const scheduleTime = new Date(after.scheduleAt).getTime();
      if (
        !Number.isFinite(scheduleTime) ||
        scheduleTime < Date.now() + SCHEDULE_MIN_LEAD_MS
      ) {
        throw new Error('Schedule at least 20 minutes from now');
      }
      return confirmScheduleAPI(
        campaign._id,
        new Date(after.scheduleAt).toISOString(),
        after.subject.trim(),
      );
    },
    onSuccess: (campaign) => {
      draft.setScheduleLocked(true);
      draft.setCampaignId(campaign._id);
      draft.setCampaignStatus(campaign.status);
      applyCampaignScheduleToDraft(campaign);
      setConfirmOpen(false);
      invalidateCampaigns();
      toast.success('Schedule locked and queued');
    },
    onError: (err: Error) => {
      setAudienceCount(null);
      toast.error(err.message || 'Schedule failed');
    },
  });

  const unlockMutation = useMutation({
    mutationFn: async () => {
      if (!draft.campaignId) {
        throw new Error('No campaign to unlock');
      }
      return unlockScheduleAPI(draft.campaignId);
    },
    onSuccess: (campaign) => {
      draft.setScheduleLocked(false);
      draft.setCampaignStatus(campaign.status);
      applyCampaignScheduleToDraft(campaign);
      invalidateCampaigns();
      toast.success('Schedule unlocked');
    },
    onError: (err: Error) => toast.error(err.message || 'Unlock failed'),
  });

  const saveDefaultsMutation = useMutation({
    mutationFn: () =>
      updateComposerDefaultsAPI({
        fromName: draft.fromName,
        replyToEmail: draft.replyToEmail,
        batchSize: draft.batchSize,
        openTracking: draft.openTracking,
        clickTracking: draft.clickTracking,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMAIL_COMPOSER_DEFAULTS(studioId),
      });
      draft.setOverrideDefaults(false);
      setDefaultsOpen(false);
      toast.success('Defaults saved for this studio');
    },
    onError: (err: Error) => toast.error(err.message || 'Save defaults failed'),
  });

  const startBlankCampaignComposer = () => {
    schedulePersistGenerationRef.current += 1;
    draft.setCampaignId(null);
    draft.setCampaignStatus(null);
    draft.setScheduleLocked(false);
    draft.setScheduleAt(null);
    draft.setActiveTemplate({ id: null, name: '', category: 'general' });
    draft.setSubject('');
    draft.setTagOptions([]);
    draft.setClassOptions([]);
    draft.setEventOptions([]);
    draft.setDesignJson(null);
    draft.setHtml(null);
    editorRef.current?.loadBlank();
    setAudienceCount(null);
    setPreviewData(null);
  };

  const hydrateSendDraftForTemplate = async (templateId: string) => {
    try {
      const sends = await listCampaignsAPI(undefined, templateId);
      const sendDraft =
        sends.find((row) => isCampaignEditable(row.status)) ?? null;
      if (!sendDraft) {
        return;
      }
      draft.setCampaignId(sendDraft._id);
      draft.setCampaignStatus(sendDraft.status);
      draft.setScheduleLocked(Boolean(sendDraft.scheduleLockedAt));
      draft.setSubject(sendDraft.subject);
      applyCampaignScheduleToDraft(sendDraft);
    } catch {
      // Template scheduleAt remains the fallback.
    }
  };

  const applyLoadedCampaign = async (campaign: EmailCampaignTemplate) => {
    schedulePersistGenerationRef.current += 1;
    draft.setCampaignId(null);
    draft.setCampaignStatus(null);
    draft.setScheduleLocked(false);
    draft.setSubject('');
    draft.setDesignJson(campaign.designJson);
    draft.setActiveTemplate({
      id: campaign._id,
      name: campaign.name,
      category: campaign.category,
    });
    applyTemplateAudience(draft, campaign.audience);
    editorRef.current?.loadDesign(campaign.designJson);
    setAudienceCount(null);
    setPreviewData(null);

    const fromTemplate = campaignScheduleToIso(campaign.scheduleAt);
    draft.setScheduleAt(fromTemplate);

    await hydrateSendDraftForTemplate(campaign._id);
  };

  const updateTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!useEmailDraftStore.getState().activeTemplateId) {
        throw new Error('No campaign selected to update');
      }
      const { design, html } = await exportEditorContent();
      const latest = useEmailDraftStore.getState();
      if (!latest.activeTemplateId) {
        throw new Error('No campaign selected to update');
      }
      return updateCampaignTemplateAPI(latest.activeTemplateId, {
        name: latest.activeTemplateName.trim() || 'Untitled campaign',
        category: (latest.activeTemplateCategory ||
          'custom') as EmailCampaignTemplateCategory,
        designJson: design,
        htmlSnapshot: html,
        audience: snapshotTemplateAudience(latest),
        ...scheduleAtForApi(latest.scheduleAt),
      });
    },
    onSuccess: (template) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMAIL_CAMPAIGN_TEMPLATES(studioId),
      });
      draft.setActiveTemplate({
        id: template._id,
        name: template.name,
        category: template.category,
      });
      const iso = campaignScheduleToIso(template.scheduleAt);
      if (iso) {
        draft.setScheduleAt(iso);
      }
      toast.success(`Updated “${template.name}”`);
    },
    onError: (err: Error) =>
      toast.error(err.message || 'Update current campaign failed'),
  });

  const saveAsNewTemplateMutation = useMutation({
    mutationFn: async () => {
      const { design, html } = await exportEditorContent();
      const latest = useEmailDraftStore.getState();
      return createCampaignTemplateAPI({
        name: templateName.trim() || 'Untitled campaign',
        category: templateCategory,
        designJson: design,
        htmlSnapshot: html,
        audience: snapshotTemplateAudience(latest),
        ...scheduleAtForApi(latest.scheduleAt),
      });
    },
    onSuccess: (template) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EMAIL_CAMPAIGN_TEMPLATES(studioId),
      });
      draft.setActiveTemplate({
        id: template._id,
        name: template.name,
        category: template.category,
      });
      setSaveTemplateOpen(false);
      setTemplateName('');
      toast.success('Campaign saved');
    },
    onError: (err: Error) => toast.error(err.message || 'Save campaign failed'),
  });

  // const fromUnlayerMutation = useMutation({
  //   mutationFn: (unlayerTemplateId: string) =>
  //     createTemplateFromUnlayerAPI({ unlayerTemplateId }),
  //   onSuccess: (template) => {
  //     void queryClient.invalidateQueries({
  //       queryKey: QUERY_KEYS.EMAIL_CAMPAIGN_TEMPLATES(studioId),
  //     });
  //     applyLoadedCampaign(template);
  //     toast.success(`Loaded “${template.name}”`);
  //   },
  //   onError: (err: Error) => toast.error(err.message || 'Import failed'),
  // });

  const loadCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => getCampaignTemplateAPI(campaignId),
    onSuccess: (campaign) => {
      void applyLoadedCampaign(campaign).then(() => {
        toast.success(`Loaded “${campaign.name}”`);
      });
    },
    onError: (err: Error) =>
      toast.error(err.message || 'Failed to load campaign'),
  });

  const loadStudioTemplate = (template: EmailCampaignTemplate) => {
    loadCampaignMutation.mutate(template._id);
  };

  const openSaveAsNewCampaignDialog = () => {
    const latest = useEmailDraftStore.getState();
    setTemplateName(latest.activeTemplateName || '');
    setTemplateCategory(
      (latest.activeTemplateCategory ||
        'general') as EmailCampaignTemplateCategory,
    );
    setSaveTemplateOpen(true);
  };

  const scheduleDate = (() => {
    if (!draft.scheduleAt) {
      return undefined;
    }
    const d = new Date(draft.scheduleAt);
    return Number.isFinite(d.getTime()) ? d : undefined;
  })();
  const audienceDisabled =
    isCampaignComplete ||
    draft.scheduleLocked ||
    loadCampaignMutation.isPending;
  // Subject/design must stay frozen while locked — BE rejects those PATCHes.
  const contentDisabled =
    isCampaignComplete ||
    draft.scheduleLocked ||
    loadCampaignMutation.isPending;

  return (
    <div className='flex h-[calc(100vh-4rem)] w-full flex-col'>
      <div className='border-b border-border px-4 pb-4 pt-4'>
        <div className='flex items-center gap-3'>
          <Button variant='ghost' size='icon' asChild>
            <Link to='/dashboard'>
              <CircleArrowLeft className='size-5' />
            </Link>
          </Button>
          <h1 className='text-2xl font-semibold'>Campaign Builder</h1>
        </div>

        <div className='mt-3 flex flex-col gap-2 sm:flex-row sm:items-center'>
          <div
            className={
              campaignPanelOpen
                ? 'flex shrink-0 items-center lg:w-[360px]'
                : 'flex shrink-0 items-center'
            }
          >
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='hidden lg:inline-flex'
              aria-expanded={campaignPanelOpen}
              onClick={() => {
                setCampaignPanelOpen((open) => !open);
              }}
            >
              {campaignPanelOpen ? (
                <>
                  <PanelLeftClose className='mr-2 size-4' />
                  Hide panel
                </>
              ) : (
                <>
                  <PanelLeft className='mr-2 size-4' />
                  Show panel
                </>
              )}
            </Button>
            <Button
              type='button'
              variant='outline'
              size='icon'
              className='lg:hidden'
              aria-expanded={campaignPanelOpen}
              aria-label={
                campaignPanelOpen
                  ? 'Hide campaign panel'
                  : 'Show campaign panel'
              }
              onClick={() => {
                setCampaignPanelOpen((open) => !open);
              }}
            >
              {campaignPanelOpen ? (
                <PanelLeftClose className='size-4' />
              ) : (
                <PanelLeft className='size-4' />
              )}
            </Button>
          </div>

          <div className='flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2'>
          <Button variant='outline' asChild>
            <Link to='/email-campaigns' className='inline-flex items-center'>
              <LayoutDashboard className='mr-2 size-4' />
              Dashboard
            </Link>
          </Button>
          <Button
            variant='outline'
            onClick={() => {
              setDefaultsOpen(true);
            }}
          >
            <Settings className='mr-2 size-4' />
            Settings
          </Button>
          <Button
            variant='outline'
            onClick={() => {
              previewMutation.mutate();
            }}
            disabled={previewMutation.isPending}
          >
            <Users className='mr-2 size-4' />
            Audience
            {audienceCount !== null ? ` (${audienceCount})` : ''}
          </Button>
          {draft.activeTemplateId ? (
            <Button
              variant='outline'
              onClick={() => {
                updateTemplateMutation.mutate();
              }}
              disabled={
                isCampaignComplete || updateTemplateMutation.isPending
              }
            >
              <Save className='mr-2 size-4' />
              Update Campaign
            </Button>
          ) : null}
          <Button
            variant='outline'
            onClick={() => {
              if (isCampaignComplete) {
                return;
              }
              openSaveAsNewCampaignDialog();
            }}
            disabled={isCampaignComplete}
          >
            <Save className='mr-2 size-4' />
            Save as new
          </Button>
          <Button
            variant='outline'
            onClick={() => {
              if (isCampaignComplete) {
                return;
              }
              startBlankCampaignComposer();
            }}
            disabled={isCampaignComplete}
          >
            <Plus className='mr-2 size-4' />
            New Campaign
          </Button>
          <Button
            onClick={() => {
              if (isCampaignComplete) {
                return;
              }
              if (!draft.subject.trim()) {
                toast.error('Enter an email subject before scheduling');
                return;
              }
              if (!hasAudienceSelection(draft)) {
                toast.error(
                  'Select at least one tag, class, or event for the audience',
                );
                return;
              }
              if (!draft.scheduleAt) {
                toast.error('Choose a schedule date/time first');
                return;
              }
              setConfirmOpen(true);
            }}
            disabled={isCampaignComplete || confirmScheduleMutation.isPending}
          >
            {draft.scheduleLocked ? (
              <CalendarClock className='mr-2 size-4' />
            ) : (
              <CalendarCheck className='mr-2 size-4' />
            )}
            {draft.scheduleLocked ? 'Change Schedule' : 'Confirm schedule'}
          </Button>
          </div>
        </div>
      </div>

      {isCampaignComplete && campaignStatus ? (
        <div className='border-b border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground'>
          This campaign is <strong className='text-foreground'>{campaignStatus}</strong>{' '}
          and can no longer be edited. Create a new campaign or load another to
          make changes.
        </div>
      ) : null}

      <div
        className={
          campaignPanelOpen
            ? 'grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[360px_1fr]'
            : 'grid min-h-0 flex-1 grid-cols-1 gap-0'
        }
      >
        {campaignPanelOpen ? (
        <aside className='space-y-4 overflow-y-auto border-r border-border p-4'>
          <div className='space-y-2'>
            <Label htmlFor='subject'>Subject</Label>
            <Input
              id='subject'
              value={draft.subject}
              onChange={(e) => {
                draft.setSubject(e.target.value);
              }}
              onBlur={() => {
                const { campaignId, subject, campaignStatus, scheduleLocked } =
                  useEmailDraftStore.getState();
                if (
                  !campaignId ||
                  !isCampaignEditable(campaignStatus) ||
                  scheduleLocked ||
                  !subject.trim()
                ) {
                  return;
                }
                void updateCampaignAPI(campaignId, {
                  subject: subject.trim(),
                }).catch(() => {
                  // Confirm / ensureCampaign will retry.
                });
              }}
              placeholder='Email subject'
              disabled={contentDisabled}
            />
          </div>

          <div className='space-y-2'>
            <Label>Tags</Label>
            <p className='text-xs text-muted-foreground'>
              Contacts with any of the selected tags (if any).
            </p>
            <TagsSelector
              value={draft.tagOptions}
              onChange={(opts) => { draft.setTagOptions(opts ?? []); }}
              isDisabled={audienceDisabled}
            />
          </div>

          <div className='space-y-2'>
            <Label>Classes</Label>
            <p className='text-xs text-muted-foreground'>
              Parents of students enrolled in any selected class (if any).
            </p>
            <MultiClassAudienceSelector
              value={draft.classOptions}
              onChange={(opts) => { draft.setClassOptions(opts); }}
              disabled={audienceDisabled}
            />
          </div>

          <div className='space-y-2'>
            <Label>Events</Label>
            <p className='text-xs text-muted-foreground'>
              Parents of students enrolled in any selected event (if any).
            </p>
            <MultiEventAudienceSelector
              value={draft.eventOptions}
              onChange={(opts) => { draft.setEventOptions(opts); }}
              disabled={audienceDisabled}
            />
          </div>

          <p className='rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
            Recipients are the unique contacts matching any selected tags,
            classes, or events. Select at least one audience filter before
            scheduling.
          </p>

          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <Label>Schedule</Label>
              {isCampaignComplete ? (
                <span className='flex items-center text-xs text-muted-foreground'>
                  <Lock className='mr-1 size-3' />
                  Read-only
                </span>
              ) : draft.scheduleLocked ? (
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={() => { unlockMutation.mutate(); }}
                  disabled={unlockMutation.isPending}
                >
                  <Unlock className='mr-1 size-4' />
                  Unlock
                </Button>
              ) : (
                <span className='flex items-center text-xs text-muted-foreground'>
                  <Lock className='mr-1 size-3' />
                  Unlocked
                </span>
              )}
            </div>
            <DateTimePicker
              key={draft.campaignId ?? draft.activeTemplateId ?? 'new'}
              value={scheduleDate}
              onChange={(date) => {
                const iso = date ? date.toISOString() : null;
                const generation = schedulePersistGenerationRef.current + 1;
                schedulePersistGenerationRef.current = generation;
                const persistContext = {
                  activeTemplateId:
                    useEmailDraftStore.getState().activeTemplateId,
                  campaignId: useEmailDraftStore.getState().campaignId,
                };
                draft.setScheduleAt(iso);
                void (async () => {
                  if (generation !== schedulePersistGenerationRef.current) {
                    return;
                  }
                  if (
                    !persistContext.campaignId &&
                    scheduleContextMatches(persistContext)
                  ) {
                    await ensureSendDraftForSchedule(iso);
                  }
                  if (
                    generation !== schedulePersistGenerationRef.current ||
                    !scheduleContextMatches(persistContext)
                  ) {
                    return;
                  }
                  await persistTemplateScheduleAt(iso);
                  if (
                    generation !== schedulePersistGenerationRef.current ||
                    !scheduleContextMatches(persistContext)
                  ) {
                    return;
                  }
                  await persistDraftScheduleAt(iso);
                })();
              }}
              disabled={audienceDisabled}
              disabledDays={{ before: new Date() }}
              hourCycle={12}
              granularity='minute'
              placeholder='Pick a send date and time'
            />
            <p className='text-xs text-muted-foreground'>
              Emails go out at the scheduled time after you confirm. Send time
              must be at least 20 minutes from now. With subject and audience set,
              changing the time saves to your send draft automatically.
            </p>
            {draft.scheduleLocked && draft.scheduleAt ? (
              <p className='rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900'>
                Locked for{' '}
                <strong>
                  {new Date(draft.scheduleAt).toLocaleString()}
                </strong>
                .
              </p>
            ) : null}
          </div>

          {draft.activeTemplateId ? (
            <p className='rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground'>
              Active campaign:{' '}
              <span className='font-medium text-foreground'>
                {draft.activeTemplateName || 'Untitled'}
              </span>
              . Tags, classes, and events below belong to this campaign. Use{' '}
              <strong>Update current campaign</strong> to save audience changes, or{' '}
              <strong>Save as new</strong> to keep both.
            </p>
          ) : null}

          <StudioTemplatesPanel
            studioId={studioId}
            templates={templatesQuery.data ?? []}
            activeTemplateId={draft.activeTemplateId}
            onLoad={loadStudioTemplate}
            onUpdated={(template) => {
              if (draft.activeTemplateId === template._id) {
                draft.setActiveTemplate({
                  id: template._id,
                  name: template.name,
                  category: template.category,
                });
              }
            }}
            onDuplicated={(template) => {
              loadStudioTemplate(template);
            }}
            onDeleted={(templateId) => {
              if (draft.activeTemplateId === templateId) {
                draft.setActiveTemplate({ id: null });
              }
            }}
          />

          {/* Unlayer Starters — hidden for now
          <UnlayerStartersPanel
            templates={unlayerTemplatesQuery.data?.data ?? []}
            isLoading={unlayerTemplatesQuery.isLoading}
            isError={unlayerTemplatesQuery.isError}
            error={unlayerTemplatesQuery.error}
            isImporting={fromUnlayerMutation.isPending}
            onImport={(id) => {
              if (isCampaignComplete) {
                return;
              }
              fromUnlayerMutation.mutate(id);
            }}
          />
          */}
        </aside>
        ) : null}

        {unlayerIdentityQuery.data ? (
          <UnlayerEditor
            editorRef={editorRef}
            designJson={draft.designJson}
            readOnly={isCampaignComplete || draft.scheduleLocked}
            userIdentity={unlayerIdentityQuery.data}
          />
        ) : (
          <div className='flex min-h-0 flex-1 items-center justify-center border border-border p-6 text-center text-sm text-muted-foreground'>
            {unlayerIdentityErrorMessage ?? 'Loading email editor…'}
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm schedule</DialogTitle>
          </DialogHeader>
          <div className='space-y-2 text-sm'>
            <p>
              This will lock the scheduled send and queue delivery. Accidental
              date changes will be blocked until you unlock.
            </p>
            <p>
              <strong>When:</strong>{' '}
              {draft.scheduleAt
                ? new Date(draft.scheduleAt).toLocaleString()
                : '—'}
            </p>
            <p>
              <strong>Subject:</strong> {draft.subject || '—'}
            </p>
            <p>
              <strong>Audience estimate:</strong>{' '}
              {audienceCount ?? 'Preview to refresh'}
            </p>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => { setConfirmOpen(false); }}>
              Cancel
            </Button>
            <Button
              onClick={() => { confirmScheduleMutation.mutate(); }}
              disabled={confirmScheduleMutation.isPending}
            >
              Lock & schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={previewOpen}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) {
            setPreviewData(null);
          }
        }}
      >
        <DialogContent className='flex max-h-[85vh] max-w-5xl flex-col gap-0 overflow-hidden p-0'>
          <DialogHeader className='border-b border-border px-6 py-4'>
            <DialogTitle>
              Audience preview
              {previewData ? ` (${previewData.count})` : ''}
            </DialogTitle>
            {draft.activeTemplateName ? (
              <p className='text-sm text-muted-foreground'>
                Campaign “{draft.activeTemplateName}” — contacts matching this
                campaign’s tags, classes, and events.
              </p>
            ) : (
              <p className='text-sm text-muted-foreground'>
                Contacts matching the current tag, class, and event selections.
                Select a campaign to load its saved audience.
              </p>
            )}
          </DialogHeader>
          <div className='min-h-0 flex-1 overflow-y-auto px-6 py-4'>
            <AudiencePreviewTable
              recipients={previewData?.recipients ?? []}
              tagOptions={draft.tagOptions}
              classOptions={draft.classOptions}
              eventOptions={draft.eventOptions}
            />
          </div>
          <DialogFooter className='border-t border-border px-6 py-4'>
            <Button variant='outline' onClick={() => { setPreviewOpen(false); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create a new campaign</DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <Input
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
              }}
              placeholder='Campaign name'
            />
            <Select
              value={templateCategory}
              onValueChange={(v) => {
                setTemplateCategory(v as EmailCampaignTemplateCategory);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder='Category' />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setSaveTemplateOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                saveAsNewTemplateMutation.mutate();
              }}
              disabled={saveAsNewTemplateMutation.isPending}
            >
              Save new campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={defaultsOpen} onOpenChange={setDefaultsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Persistent studio defaults</DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <Input
              value={draft.fromName}
              onChange={(e) => { draft.setFromName(e.target.value); }}
              placeholder='From name'
            />
            <Input
              value={draft.replyToEmail}
              onChange={(e) => { draft.setReplyToEmail(e.target.value); }}
              placeholder='Reply-to email'
            />
            <p className='text-xs text-muted-foreground'>
              Defaults from your studio CRM email when empty. You can override
              here; if left blank at send time, the CRM email is used again.
            </p>
            <div className='space-y-1'>
              <Label htmlFor='defaults-batch-size'>Batch size</Label>
              <p className='text-xs text-muted-foreground'>
                Recipients processed per send batch. Default is 50. Max is 250.
              </p>
              <Input
                id='defaults-batch-size'
                type='number'
                min={1}
                max={250}
                value={draft.batchSize}
                onChange={(e) =>
                  { draft.setBatchSize(Number(e.target.value) || 50); }
                }
                placeholder='50'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => { saveDefaultsMutation.mutate(); }}
              disabled={saveDefaultsMutation.isPending}
            >
              Save defaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
